(() => {
  if (customElements.get('cart-benefits')) return;

  const KIT_WEIGHT = 2;
  const DEFAULT_WEIGHT = 1;

  class CartBenefits extends HTMLElement {
    constructor() {
      super();
      this.isProcessing = false;
    }

    connectedCallback() {
      this.syncGift();
    }

    getFetchConfig() {
      if (typeof fetchConfig === 'function') return fetchConfig();
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      };
    }

    isCartPage() {
      const route = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart').replace(/\/$/, '');
      const path = window.location.pathname.replace(/\/$/, '');
      return (
        document.body.classList.contains('template-cart') ||
        Boolean(document.querySelector('cart-items.cart-page, .cart-page cart-items, #main-cart-items')) ||
        path === route ||
        path.endsWith('/cart')
      );
    }

    async fetchCart() {
      const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }

    getTiers() {
      try {
        const parsed = JSON.parse(this.dataset.giftTiers || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    getAllGiftVariantIds() {
      return new Set(this.getTiers().map((tier) => String(tier.variantId)).filter(Boolean));
    }

    isGiftCartItem(item) {
      const properties = item.properties || {};
      if (properties._celina_gift === 'true' || properties._celina_gift === true) return true;
      const variantId = String(item.variant_id || item.id || '');
      return this.getAllGiftVariantIds().has(variantId);
    }

    getGiftItems(cart) {
      return (cart.items || []).filter((item) => this.isGiftCartItem(item));
    }

    getPaidItems(cart) {
      return (cart.items || []).filter((item) => !this.isGiftCartItem(item));
    }

    getPaidSubtotal(cart) {
      return this.getPaidItems(cart).reduce((total, item) => total + (item.final_line_price || 0), 0);
    }

    getWeightedUnits(cart) {
      return this.getPaidItems(cart).reduce((total, item) => {
        const sku = String(item.sku || '').trim().toUpperCase();
        const weight = sku.startsWith('KIT') ? KIT_WEIGHT : DEFAULT_WEIGHT;
        return total + weight * Number(item.quantity || 0);
      }, 0);
    }

    resolveTier(units) {
      if (units >= 3) return 3;
      if (units === 2) return 2;
      if (units === 1) return 1;
      return 0;
    }

    resolveGiftVariant(tier) {
      const tiers = this.getTiers();
      for (let candidate = tier; candidate >= 1; candidate -= 1) {
        const match = tiers.find((entry) => Number(entry.tier) === candidate);
        if (match && match.variantId && match.available !== false) {
          return String(match.variantId);
        }
      }
      return '';
    }

    async removeGiftItems(cart) {
      const removedKeys = new Set();
      for (const item of this.getGiftItems(cart)) {
        if (!item.key || removedKeys.has(item.key)) continue;
        removedKeys.add(item.key);
        await fetch(window.routes.cart_change_url, {
          ...this.getFetchConfig(),
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
    }

    async addGiftVariant(variantId) {
      const response = await fetch(window.routes.cart_add_url, {
        ...this.getFetchConfig(),
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: { _celina_gift: 'true' },
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Nao foi possivel adicionar o brinde.');
      }
    }

    async syncGift() {
      if (this.dataset.giftEnabled !== 'true') return;
      if (this.isProcessing || window.CelinaGiftSyncInProgress) return;

      const threshold = Number(this.dataset.giftThreshold || 0);
      if (!threshold) return;

      window.CelinaGiftSyncInProgress = true;
      this.isProcessing = true;

      try {
        const cart = await this.fetchCart();
        const giftItems = this.getGiftItems(cart);
        const paidSubtotal = this.getPaidSubtotal(cart);

        if (paidSubtotal < threshold) {
          if (giftItems.length) {
            await this.removeGiftItems(cart);
            this.refreshCartUI();
          }
          return;
        }

        const units = this.getWeightedUnits(cart);
        const tier = this.resolveTier(units);
        const targetVariantId = this.resolveGiftVariant(tier);

        if (!targetVariantId) {
          if (giftItems.length) {
            await this.removeGiftItems(cart);
            this.refreshCartUI();
          }
          return;
        }

        const currentIds = giftItems.map((item) => String(item.variant_id || item.id || ''));
        const isCorrect =
          giftItems.length === 1 &&
          Number(giftItems[0].quantity || 0) === 1 &&
          currentIds[0] === targetVariantId;

        if (isCorrect) return;

        if (giftItems.length) {
          await this.removeGiftItems(cart);
        }

        try {
          await this.addGiftVariant(targetVariantId);
        } catch (error) {
          console.warn('[Celina cart benefits] Gift unavailable', error);
        }

        this.refreshCartUI();
      } catch (error) {
        console.warn('[Celina cart benefits] Gift sync failed', error);
      } finally {
        this.isProcessing = false;
        window.CelinaGiftSyncInProgress = false;
      }
    }

    refreshCartUI() {
      if (this.isCartPage()) {
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-benefits' });
      }
    }
  }

  customElements.define('cart-benefits', CartBenefits);

  function celinaCartBenefitsFetchConfig() {
    if (typeof fetchConfig === 'function') return fetchConfig();
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  function celinaCartBenefitsIsCartPage() {
    const route = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart').replace(/\/$/, '');
    const path = window.location.pathname.replace(/\/$/, '');
    return document.body.classList.contains('template-cart') || Boolean(document.querySelector('cart-items.cart-page, #main-cart-items')) || path === route || path.endsWith('/cart');
  }

  function celinaCartBenefitsRefreshCart() {
    if (celinaCartBenefitsIsCartPage()) {
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-cross-sell' });
    }
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-cross-sell-add]');
    if (!button) return;

    event.preventDefault();
    if (button.disabled || button.classList.contains('is-loading')) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...celinaCartBenefitsFetchConfig(),
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Nao foi possivel adicionar este produto.');
      }
      celinaCartBenefitsRefreshCart();
    } catch (error) {
      console.warn('[Celina cart cross-sell] Add failed', error);
      alert(error.message || 'Nao foi possivel adicionar este produto. Tente novamente.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });
})();
